import { get, patch, post } from '../../shared/apiClient';
import type { FlowScreenListItem } from '../../components/questionnaire/QuestionnaireNavSidebar';
import {
  persistFlowSession,
  readStoredFlowSession,
  type FlowMode,
  type QuestionnaireSessionBackResponse,
  type QuestionnaireSessionNextResponse,
  type QuestionnaireSessionStartResponse,
  type SessionScreen,
  type StoredFlowSession,
} from './session';

export interface FlowStatusResponse {
  loaded: boolean;
  screens_count: number;
}

export interface FlowScreensApiResponse {
  total: number;
  screens: FlowScreenListItem[];
}

export interface FlowStartRequestBody {
  country?: string | null;
  tax_year: number;
  session_id?: string;
  start_at_screen_id?: string;
  region?: string;
  skip_identification?: boolean;
}

export interface FlowNextRequestBody {
  session_id: string;
  answers: Record<string, unknown>;
}

export interface FlowBackRequestBody {
  session_id: string;
}

export interface FlowGoToRequestBody {
  session_id: string;
  screen_id: string;
}

export interface FlowGoToResponse {
  success: boolean;
  country: string | null;
  screen: SessionScreen | null;
  visited_screen_ids: string[] | null;
  error: string | null;
}

export interface FlowSessionResumeResponse {
  session_id: string;
  country: string | null;
  screen: QuestionnaireSessionStartResponse['screen'];
  answers: Record<string, unknown>;
  visited_screen_ids: string[];
  language?: string;
}

export async function fetchFlowStatus(): Promise<FlowStatusResponse> {
  const { data } = await get<FlowStatusResponse>('/flow/status');
  return data;
}

export async function fetchFlowScreens(params?: {
  country?: string;
  category?: string;
  region?: string;
  session_id?: string;
}): Promise<FlowScreensApiResponse> {
  const { data } = await get<FlowScreensApiResponse>('/flow/screens', params);
  return data;
}

export async function flowStart(body: FlowStartRequestBody): Promise<QuestionnaireSessionStartResponse> {
  const { data } = await post<FlowStartRequestBody, QuestionnaireSessionStartResponse>('/flow/start', body);
  return data;
}

export async function flowNext(body: FlowNextRequestBody): Promise<QuestionnaireSessionNextResponse> {
  const { data } = await post<FlowNextRequestBody, QuestionnaireSessionNextResponse>('/flow/next', body);
  return data;
}

export async function flowBack(body: FlowBackRequestBody): Promise<QuestionnaireSessionBackResponse> {
  const { data } = await post<FlowBackRequestBody, QuestionnaireSessionBackResponse>('/flow/back', body);
  return data;
}

export async function flowGoTo(body: FlowGoToRequestBody): Promise<FlowGoToResponse> {
  const { data } = await post<FlowGoToRequestBody, FlowGoToResponse>('/flow/go_to', body);
  return data;
}

export async function flowResumeAt(sessionId: string, screenId: string): Promise<FlowGoToResponse> {
  const { data } = await post<FlowGoToRequestBody, FlowGoToResponse>(
    `/flow/session/${encodeURIComponent(sessionId)}/resume_at`,
    { screen_id: screenId, session_id: sessionId }
  );
  return data;
}

export async function fetchFlowSession(sessionId: string): Promise<FlowSessionResumeResponse> {
  const { data } = await get<FlowSessionResumeResponse>(`/flow/session/${encodeURIComponent(sessionId)}`);
  return data;
}

export interface MyFlowSessionResponse {
  session_id: string | null;
  country: string | null;
  tax_year: number | null;
}

export async function fetchMyFlowSession(): Promise<MyFlowSessionResponse> {
  const { data } = await get<MyFlowSessionResponse>('/flow/me/session');
  return data;
}

/** Read local session id; for logged-in users, fall back to the server if missing. */
export async function resolveStoredFlowSession(
  mode: FlowMode,
  userId: string | null | undefined,
  defaultTaxYear: number,
): Promise<StoredFlowSession> {
  const stored = readStoredFlowSession(mode, userId);
  if (mode !== 'identified' || !userId || stored.sessionId) {
    return { ...stored, taxYear: stored.taxYear ?? defaultTaxYear };
  }
  try {
    const mine = await fetchMyFlowSession();
    if (!mine.session_id) return { ...stored, taxYear: defaultTaxYear };
    persistFlowSession(mine.session_id, mine.country, mine.tax_year ?? defaultTaxYear, mode, userId);
    return readStoredFlowSession(mode, userId);
  } catch {
    return { ...stored, taxYear: defaultTaxYear };
  }
}

export async function flowSetLanguage(
  sessionId: string,
  language: string
): Promise<void> {
  await patch<{ language: string }, { language: string }>(
    `/flow/${encodeURIComponent(sessionId)}/language`,
    { language }
  );
}
